import { useNavigate } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import TablePagination from '@mui/material/TablePagination';
import { flexRender } from '@tanstack/react-table';
import {
  AppTable,
  AppTableBody,
  AppTableCell,
  AppTableContainer,
  AppTableHead,
  AppTableRow,
  AppTableSort,
} from '../../../../components/common/app-table/ui';
import useDefaultTableERF from '../../../../components/common/app-table/useDefaultTableERF';
import LoadingIndicator from '../../../../components/common/LoadingIndicator';
import FormAddButton from '../../../../components/common/FormAddButton';
import AppSearchField from '../../../../components/common/forms/AppSearchField';
import { ExportButton } from '../../../../components/common/primebmd-ui/DataButtons';
import { useAuth } from '../../../../components/AuthProvider';
import { IERFColumns, IERF, IERFExport } from '../utils/types';
import { useGetEPFERFQuery, useFormatGetIERFQuery } from '../../../../hooks/useEPFERF';
import { useCreatePMColumns, useCreateEMColumns } from '../hooks/useEPFERFColumns';
import { exportIERFToExcel } from '../utils/exporting';

const EPFERFTable = () => {
  const navigate = useNavigate();
  const user = useAuth();
  const rolePermissions = user?.currentUserRole?.permissions;
  const hasReqManagementPermission = rolePermissions && rolePermissions.some((permission) => ["REQ::MANAGEMENT"].includes(permission));
  // const hasAllocManagementPermission = rolePermissions && rolePermissions.some((permission) => ["ALLOC::MANAGEMENT"].includes(permission));
  const pmColumns = useCreatePMColumns();
  const emColumns = useCreateEMColumns();
  const erf = useGetEPFERFQuery({ 
    // role: hasReqManagementPermission ? "REQ::MANAGEMENT" : hasAllocManagementPermission ? "ALLOC::MANAGEMENT" : "",
    page: 'ERF' 
  });
  const data: IERF[] = erf?.data === undefined ? [] : erf?.data;

  const formattedIERFData: IERFExport[] = useFormatGetIERFQuery(data);
  const { table, dataCount, searchFilter, setSearchFilter, pageIndex, pageSize } =
  useDefaultTableERF<IERFColumns>(
    data, 
    hasReqManagementPermission ? pmColumns : emColumns
  );

  const emptyText = searchFilter ? 'No data found' : 'No data yet';

  const goToAddERF = () => navigate('/my-equipment-requests/my-erf/new');

  const handleExport = () => {
    exportIERFToExcel(formattedIERFData, 'ERF Report');
  }

  if (user?.isLoading || !user?.currentUserRole || erf?.isLoading) {
    return <LoadingIndicator show />;
  }

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" mb={2} gap={2}>
        <AppSearchField
          onSearchKeywordChange={setSearchFilter}
          searchKeyword={searchFilter ?? ''}
        />
        <Stack direction="row" gap={.75}>
          { hasReqManagementPermission && <FormAddButton onClick={goToAddERF} /> }
          <ExportButton onClick={handleExport} />
        </Stack>
      </Stack>
      <Paper elevation={3}>
        <AppTableContainer>
          <AppTable stickyHeader sx={ hasReqManagementPermission ? { minWidth: '1800px' } : {}}>
            <AppTableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <AppTableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <AppTableCell key={header.id} align='center'>
                      <AppTableSort
                        canSort={header.column.getCanSort()}
                        currentSort={header.column.getIsSorted()}
                        onSort={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </AppTableSort>
                    </AppTableCell>
                  ))}
                </AppTableRow>
              ))}
            </AppTableHead>
            <AppTableBody
              dataCount={dataCount}
              // loading={loading}
              emptyText={emptyText}
            >
              {table.getRowModel().rows.map((row) => (
                <AppTableRow key={row.id} sx={{ height: '50px' }}>
                  {row.getVisibleCells().map((cell) => (
                    <AppTableCell key={cell.id} align='center'>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </AppTableCell>
                  ))}
                </AppTableRow>
              ))}
            </AppTableBody>
          </AppTable>
        </AppTableContainer>
        <Box position='relative'>
          <TablePagination
            sx={{ bgcolor: 'white', borderTop: '1px solid #eee' }}
            component='div'
            count={dataCount}
            page={pageIndex}
            onPageChange={(e, page) => table.setPageIndex(page)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => table.setPageSize(Number(e.target.value))}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </Box>
      </Paper>
    </>
  );
}

export default EPFERFTable;